import React, { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Mail, MapPin, Phone, Send, Github, Linkedin, Twitter } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from '../hooks/use-toast';
import emailjs from '@emailjs/browser';

const Contact = ({ data }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // EmailJS configuration
    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'YOUR_SERVICE_ID';
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'YOUR_TEMPLATE_ID';
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'YOUR_PUBLIC_KEY';

    // Check if EmailJS is configured
    if (serviceId === 'YOUR_SERVICE_ID' || templateId === 'YOUR_TEMPLATE_ID' || publicKey === 'YOUR_PUBLIC_KEY') {
      toast({
        title: "Configuration Needed",
        description: "Please set up EmailJS credentials in your .env file. Check EMAILJS_SETUP.md for instructions.",
        variant: "destructive"
      });
      setIsSubmitting(false);
      return;
    }

    try {
      // Send email using EmailJS
      await emailjs.send(
        serviceId,
        templateId,
        {
          from_name: formData.name,
          from_email: formData.email,
          message: formData.message,
          to_email: 'pozidis3@gmail.com'
        },
        publicKey
      );

      toast({
        title: "Message Sent!",
        description: "Thank you for reaching out. I'll get back to you soon.",
      });
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('EmailJS Error:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again or contact me directly at pozidis3@gmail.com",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-[#0F0F0F]">
      <div className="container mx-auto px-6">
        <div 
          ref={ref}
          className={`max-w-6xl mx-auto transition-all duration-1000 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-16 text-center">
            Get In <span className="text-[#0066FF]">Touch</span>
          </h2>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-white mb-4">
                  Let's work together
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  I'm always open to discussing new projects, creative ideas, or opportunities to be part of your vision.
                </p>
              </div>

              {/* Contact details */}
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#0066FF]/10 rounded-xl">
                    <Mail className="text-[#0066FF]" size={24} />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">Email</h4>
                    <p className="text-gray-400">{data.email}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#0066FF]/10 rounded-xl">
                    <Phone className="text-[#0066FF]" size={24} />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">Phone</h4>
                    <p className="text-gray-400">{data.phone}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#0066FF]/10 rounded-xl">
                    <MapPin className="text-[#0066FF]" size={24} />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">Location</h4>
                    <p className="text-gray-400">{data.location}</p>
                  </div>
                </div>
              </div>

              {/* Social links */}
              <div className="pt-8">
                <h4 className="text-white font-medium mb-4">Follow me</h4>
                <div className="flex gap-4">
                  <a 
                    href={data.social.github} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-3 bg-[#1A1A1A] rounded-xl border border-white/10 hover:border-[#0066FF]/50 text-gray-400 hover:text-[#0066FF] transition-all duration-300 hover:transform hover:scale-110"
                  >
                    <Github size={24} />
                  </a>
                  <a 
                    href={data.social.linkedin} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-3 bg-[#1A1A1A] rounded-xl border border-white/10 hover:border-[#0066FF]/50 text-gray-400 hover:text-[#0066FF] transition-all duration-300 hover:transform hover:scale-110"
                  >
                    <Linkedin size={24} />
                  </a>
                  <a 
                    href={data.social.twitter} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-3 bg-[#1A1A1A] rounded-xl border border-white/10 hover:border-[#0066FF]/50 text-gray-400 hover:text-[#0066FF] transition-all duration-300 hover:transform hover:scale-110"
                  >
                    <Twitter size={24} />
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-[#1A1A1A] rounded-2xl p-8 border border-white/10">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-white font-medium mb-2">
                    Name
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="bg-[#0A0A0A] border-white/10 text-white focus:border-[#0066FF] focus:ring-[#0066FF] rounded-lg"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-white font-medium mb-2">
                    Email
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="bg-[#0A0A0A] border-white/10 text-white focus:border-[#0066FF] focus:ring-[#0066FF] rounded-lg"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-white font-medium mb-2">
                    Message
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    className="bg-[#0A0A0A] border-white/10 text-white focus:border-[#0066FF] focus:ring-[#0066FF] rounded-lg resize-none"
                    placeholder="Tell me about your project..."
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#0066FF] hover:bg-[#0052CC] text-white py-6 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-[#0066FF]/50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send className="mr-2" size={20} />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
